import { useState } from 'react';
import { uniqBy } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimActionHistoryResponse } from '~/server_shared/openapi/data-contracts';

import type { ActionVersion } from './utils';

/**
 * One action's recorded versions, newest first, read a page at a time.
 *
 * The caller is mounted only while the history is open, so each opening reads afresh and nothing is
 * read with the actions list. The page size is claims-ai's to choose. No error dialog: the history
 * says the read failed and offers to try again.
 */
export const useActionHistory = (claimId: number, actionId: string) => {
  // The cursor of the page being read, and every version of the pages read before it.
  const [before, setBefore] = useState<string>();
  const [heldVersions, setHeldVersions] = useState<ActionVersion[]>([]);

  const { data, isLoading, isError, reloadData } = useDataFetcher<ClaimActionHistoryResponse>(
    `/api/v1/claims/${claimId}/actions/${actionId}/history`,
    before ? { params: { before } } : null,
    { shouldOpenErrorDialog: false }
  );

  // Deduplicated: until the next page lands, `data` is still the page the held versions end with.
  const versions = uniqBy([...heldVersions, ...(data?.versions ?? [])], 'version_id');

  const loadOlder = (): void => {
    const oldest = versions.at(-1);
    if (!oldest) return;

    setHeldVersions(versions);
    setBefore(oldest.version_id);
  };

  return { versions, hasMore: Boolean(data?.has_more), isLoading, isError, loadOlder, retry: reloadData };
};
